import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, {
  ensureValidAccessToken,
  setTokensRefreshedHandler,
  setUnauthorizedHandler,
} from '../services/api';
import {
  clearAuthStorage,
  getAccessToken,
  getRefreshToken,
  getStoredAdmin,
  normalizeToken,
  setAdmin as persistAdmin,
  setAuthRole,
  setTokens,
} from '../services/authStorage';
import { usePortal } from './PortalContext';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { isPartnerPortal } = usePortal();

  const logout = useCallback(() => {
    clearAuthStorage();
    delete api.defaults.headers.common.Authorization;
    setAdmin(null);
    setIsAuthenticated(false);
    navigate('/login', { replace: true });
  }, [navigate]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setAdmin(null);
      setIsAuthenticated(false);
      navigate('/login', { replace: true });
    });

    setTokensRefreshedHandler(() => {
      // Tokens are persisted in authStorage; keep session marked as active.
      setIsAuthenticated(true);
    });
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();
      const adminData = getStoredAdmin();

      if (!accessToken || !adminData) {
        if (!cancelled) {
          setLoading(false);
        }
        return;
      }

      try {
        if (refreshToken) {
          await ensureValidAccessToken();
        }

        const activeToken = getAccessToken();
        if (!activeToken) {
          logout();
          return;
        }

        api.defaults.headers.common.Authorization = `Bearer ${activeToken}`;

        if (!cancelled) {
          setAdmin(adminData);
          setIsAuthenticated(true);
          if (adminData?.role) {
            setAuthRole(adminData.role);
          }
        }
      } catch (error) {
        console.error('Failed to restore session:', error);
        if (!cancelled) {
          logout();
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, [logout]);

  const login = async (email, password) => {
    try {
      const response = await api.post(
        isPartnerPortal ? '/partner/login' : '/admin/login',
        { email, password },
      );
      const payload = response.data?.data || response.data || {};
      const adminUser = payload.admin || payload.user || null;
      const accessToken = normalizeToken(
        payload.accessToken ||
          payload.token ||
          payload.jwt ||
          payload.access_token ||
          payload.idToken ||
          payload?.tokens?.accessToken ||
          payload?.tokens?.access_token ||
          null,
      );
      const refreshToken = normalizeToken(payload.refreshToken || payload?.tokens?.refreshToken);

      if (!accessToken) {
        return {
          success: false,
          message: 'Login response did not include a valid access token',
        };
      }

      setTokens({ accessToken, refreshToken });
      persistAdmin(adminUser);
      setAuthRole(adminUser?.role || (isPartnerPortal ? 'partner_admin' : 'super_admin'));

      api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      setAdmin(adminUser);
      setIsAuthenticated(true);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.error?.message || 'Login failed',
      };
    }
  };

  const value = {
    isAuthenticated,
    admin,
    login,
    logout,
    loading,
    isPartnerAdmin: admin?.role === 'partner_admin' || isPartnerPortal,
    isSuperAdmin: admin?.role === 'super_admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
